import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Task, TaskPriority } from "@/v2/lib/tasks";

const PRIORITY_STYLE: Record<TaskPriority, string> = {
  urgent: "border-l-4 border-l-danger",
  high: "border-l-4 border-l-warning",
  medium: "border-l-4 border-l-accent",
  low: "border-l-4 border-l-border",
};

const PRIORITY_BADGE: Record<TaskPriority, string> = {
  urgent: "bg-danger/15 text-danger",
  high: "bg-warning/15 text-warning",
  medium: "bg-accent/15 text-accent",
  low: "bg-gray-100 text-text-muted",
};

const PRIORITY_LABEL_KEY: Record<TaskPriority, string> = {
  urgent: "v2.tasks.priority.urgent",
  high: "v2.tasks.priority.high",
  medium: "v2.tasks.priority.medium",
  low: "v2.tasks.priority.low",
};

export function TaskCard({ task }: { task: Task }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hint = task.hintKey ? t(task.hintKey, task.params) : task.hintRaw;
  return (
    <button
      type="button"
      onClick={() => navigate(task.href)}
      className={cn(
        "w-full text-left bg-surface rounded-md border border-border hover:border-accent transition-colors cursor-pointer group",
        PRIORITY_STYLE[task.priority],
      )}
    >
      <div className="flex items-start gap-3 p-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={cn(
                "text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded",
                PRIORITY_BADGE[task.priority],
              )}
            >
              {t(PRIORITY_LABEL_KEY[task.priority])}
            </span>
            <span className="text-xs text-text-muted font-medium">
              {task.reference}
            </span>
          </div>
          <div className="text-sm font-medium text-text-primary truncate">
            {t(task.titleKey, task.params)}
          </div>
          {hint && (
            <div className="text-xs text-text-muted mt-0.5 truncate">
              {hint}
            </div>
          )}
          <div className="flex items-center gap-1 mt-1.5 text-[11px] text-text-muted">
            <MapPin size={10} />
            <span className="truncate">
              {task.adresse}
              {task.commune && task.commune !== "—" ? ` · ${task.commune}` : ""}
            </span>
          </div>
        </div>
        <ChevronRight
          size={16}
          className="text-text-muted group-hover:text-accent flex-shrink-0 mt-1"
        />
      </div>
    </button>
  );
}
